import React, { useContext, useState, useEffect, useRef } from "react";
import Helmet from "react-helmet";
import { graphql, Link, navigate } from 'gatsby';
import Layout from "../components/layout";
import ThemeContext from '../context/ThemeContext';
import FluidImage from "../components/fluidImage";
import LocationCard from "../components/locationCard";
import Seo from 'gatsby-plugin-wpgraphql-seo';
import prepareSchemaHours from "../helpers/prepareSchemaHours";

import * as styleSheet from "./locations.module.scss"

const LocationsTemplate = (props) => {

	const {
		data: {
			page,
			options: {
				headerSection: {
					ACFHeaderOptions: {
						mainNavigationSettings: {
							headerLogo
						}
					}
				}

			},
			locations: {
				nodes: locations
			},
			rightArrowYellowOffsite,
			arrowRedLocal,
		}
	} = props;

	const lastElementInList = useRef();
	const selectRef = useRef();
	const zipRef = useRef();
	const { location: useLocation, getLocation, setLocation } = useContext(ThemeContext);
	const [ filterError, setFilterError ] = useState("");
	const [sortedLocations, setSortedLocations] = useState( locations );
	const [isFiltered, setIsFiltered] = useState(false);
	const [schemaData, setSchemaData] = useState(null);

	const sortByDistance = (lat, lng) => {
		const locationList = locations.map(location => ({
			...location,
			distance: getDistanceFromLatLonInMi(
				location.birdeyeLocation.location.lat,
				location.birdeyeLocation.location.lng,
				lat,
				lng
			)
		}));

		locationList.sort((a, b) => a.distance - b.distance);
		setSortedLocations(locationList);

		function getDistanceFromLatLonInMi(lat1, lon1, lat2, lon2) {
			var R = 6371;
			var dLat = deg2rad(lat2 - lat1);
			var dLon = deg2rad(lon2 - lon1);
			var a =
				Math.sin(dLat / 2) * Math.sin(dLat / 2) +
				Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
				Math.sin(dLon / 2) * Math.sin(dLon / 2);
			var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
			var d = R * c;
			return d * 0.621371;
		}

		function deg2rad(deg) {
			return deg * (Math.PI / 180);
		}
	};

	const filterLocations = async () => {

		setFilterError("");

		const zipCall = async () => {
			const zipUrl = "https://maps.googleapis.com/maps/api/geocode/json?address=" + zipRef.current.value + "&key=" + process.env.GATSBY_GOOGLE_GEOCODE_API_KEY;

			try {
				await fetch(zipUrl)
				.then(data => data.json())
				.then(data => {
					if( data && data.status === "OK"){
						if( data.results.length ){
							setLocation({
								lat: data.results[0].geometry.location.lat,
								lng: data.results[0].geometry.location.lng
							});
							setIsFiltered(true);
						} else {
							alert("Sorry, that zipcode did not return a location.")
						}
					} else {
						alert("Sorry, that zipcode did not return a location.")
					}
				});
			} catch (error) {
				console.log(error);
			}
		}

		if( selectRef && selectRef.current && selectRef.current.options.selectedIndex && selectRef.current.options[selectRef.current.options.selectedIndex].value && selectRef.current.options[selectRef.current.options.selectedIndex].value !== false ){
			navigate(selectRef.current.options[selectRef.current.options.selectedIndex].value);
		} else if( zipRef && zipRef.current && zipRef.current.value && zipRef.current.value.length === 5 ){
			zipCall();
		} else if( zipRef && zipRef.current && zipRef.current.value && zipRef.current.value.length > 0 && zipRef.current.value.length !== 5 ){
			setFilterError("zip")
		} else {
			setFilterError("both")
		}
	};

	const clearFilters = () => {
		setIsFiltered(false);
		setFilterError("");
		if (zipRef.current) {
			zipRef.current.value = "";
		}
		const alphabetical = [...locations].sort((a, b) => a.title.localeCompare(b.title));
		setSortedLocations(alphabetical);
	};

	useEffect(() => {
		if (isFiltered) {
			sortByDistance(useLocation.lat, useLocation.lng);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [useLocation, isFiltered]);
	
	useEffect(() => {
		if (locations.length < 1) {
			return;
		}

		const chenalLocation = locations.find(location => location.title.includes('Chenal'));
		const nonChenalLocations = locations.filter(location => !location.title.includes('Chenal'));

		if (!chenalLocation) {
			return;
		}

		const deparments = nonChenalLocations.map(location => {
			const deptHours = prepareSchemaHours(location.birdeyeLocation.hoursOfOperations);
			return `{
				"@context": "https://schema.org",
				"@type": "Dentist",
				"name": "${location.birdeyeLocation.name}",
				"url": "${location.link}",
				"telephone": "${location.birdeyeLocation.phone}",
				"address": {
					"@type": "PostalAddress",
					"streetAddress": "${location.birdeyeLocation.location.address1} ${location.birdeyeLocation.location.address2}",
					"addressLocality": "${location.birdeyeLocation.location.city}",
					"addressRegion": "${location.birdeyeLocation.location.state}",
					"postalCode": "${location.birdeyeLocation.location.zip}",
					"addressCountry": "US"
				},
				"geo": {
					"@type": "GeoCoordinates",
					"latitude": ${location.birdeyeLocation.location.lat},
					"longitude": ${location.birdeyeLocation.location.lng}
				},
				"openingHours": [${deptHours}]
			}`;
		});

		const chenalLocHours = prepareSchemaHours(chenalLocation.birdeyeLocation.hoursOfOperations);
		const newSchema = `{
			"@context": "https://schema.org",
			"@type": "Dentist",
			"name": "${chenalLocation.birdeyeLocation.name}",
			"url": "${chenalLocation.link}",
			"telephone": "${chenalLocation.birdeyeLocation.phone}",
			"address": {
				"@type": "PostalAddress",
				"streetAddress": "${chenalLocation.birdeyeLocation.location.address1} ${chenalLocation.birdeyeLocation.location.address2}",
				"addressLocality": "${chenalLocation.birdeyeLocation.location.city}",
				"addressRegion": "${chenalLocation.birdeyeLocation.location.state}",
				"postalCode": "${chenalLocation.birdeyeLocation.location.zip}",
				"addressCountry": "US"
			},
			"geo": {
				"@type": "GeoCoordinates",
				"latitude": ${chenalLocation.birdeyeLocation.location.lat},
				"longitude": ${chenalLocation.birdeyeLocation.location.lng}
			},
			"openingHours": [${chenalLocHours}],
			"department": [${deparments}]
		}`;

		setSchemaData(newSchema);
	}, []);

	return (
    <Layout>
			<Seo post={page} />
			<Helmet>
				{schemaData && <script type="application/ld+json">
					{schemaData}
				</script>}
			</Helmet>
			<div className={styleSheet.Locations}>
				<div className={styleSheet.container}>
					<div className={styleSheet.top}>
						<h1 className={styleSheet.screenreader}>Rock Family Dental Locations</h1>
						{ headerLogo && <Link to="/" className={styleSheet.logolink}><FluidImage image={headerLogo} passedClass={styleSheet.logo} loading={"eager"} /></Link>}
					</div>
					<div className={styleSheet.middle}>
						{ page?.Locations?.heading && <h2 className={styleSheet.heading} dangerouslySetInnerHTML={{__html: page.Locations.heading}}></h2> }
						{ page?.Locations?.blurb && <div className={styleSheet.blurb} dangerouslySetInnerHTML={{__html: page.Locations.blurb}}></div> }
					</div>
					<div className={styleSheet.bottom}>
						<div className={styleSheet.full}>
							<div className={styleSheet.filters}>
								<div className={styleSheet.LocationSelector}>
									<span className={styleSheet.heading}>Quick Select:</span>
									<div className={ (filterError === "location" || filterError === "both") ? styleSheet.selectcontainererror : styleSheet.selectcontainer}>
										<select className={styleSheet.select} ref={selectRef} aria-label="Quick select a location:" onChange={(e) => { if (e.target.value && e.target.value !== 'false') navigate(e.target.value); }}>
											<option value={false}>Choose a location</option>
											{ locations.map( (location, index) => {
												return <option key={location.id} value={location.link} aria-label={`Select ${location.title}`}  dangerouslySetInnerHTML={{__html: location.ACFLocation.menuTitle ? `${location.ACFLocation.menuTitle}` : `${location.birdeyeLocation.location.city}, ${location.birdeyeLocation.location.state}`}}></option>
											})}
										</select>	
									</div>
								</div>
								<div className={styleSheet.ZipSelector}>
									<span className={styleSheet.heading}>Search by ZIP:</span>
									<input type="text" aria-label="Search by ZIP:" ref={zipRef} className={ ( filterError === "zip" || filterError === "both") ? styleSheet.zipinputerror : styleSheet.zipinput} placeholder="Zip Code" onKeyDown={(e) => { if (e.key === 'Enter') filterLocations(); }} />
									<button className={styleSheet.go} aria-label="Go to location" onClick={(event)=>{ filterLocations();  }}>Go</button>
								</div>
							</div>
							{ isFiltered && (
								<div className={styleSheet.ClearFilters}>
									<button className={styleSheet.clearfilters} aria-label="Clear zip code filter" onClick={clearFilters}>
								<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
									<line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
									<line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
								</svg>
								Clear Filters
							</button>
								</div>
							)}
							<div className={styleSheet.cardcontainer}>
								{ sortedLocations && sortedLocations.map((location, i) => {   
									return (
										<div key={location.id} className={styleSheet.card} ref={i === (sortedLocations.length -1) ? lastElementInList : null}>
											<LocationCard key={location.id} location={location} arrowRedLocal={arrowRedLocal} rightArrowYellowOffsite={rightArrowYellowOffsite} isTeamPage={true} />
										</div>
									);
								})} 
								{ sortedLocations.length === 0 && (
									<div className={styleSheet.card}>
										<p className={styleSheet.nolocations}>No locations found nearby. Try expanding your search.</p>
									</div>	
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			
		</Layout>
    );
};

export const query = graphql`query ($id: String!) {
  page: wpPage(id: {eq: $id}) {
    title
    seo {
      title
      metaDesc
      focuskw
      metaKeywords
      metaRobotsNoindex
      metaRobotsNofollow
      opengraphTitle
      opengraphDescription
      opengraphImage {
        altText
        sourceUrl
        srcSet
      }
      twitterTitle
      twitterDescription
      twitterImage {
        altText
        sourceUrl
        srcSet
      }
      canonical
      cornerstone
      schema {
        articleType
        pageType
        raw
      }
    }
	Locations {
		heading
		blurb 
	}
  }
  options: wp {
    headerSection {
      ACFHeaderOptions {
        mainNavigationSettings {
          headerLogo {
            altText
            localFile {
              childImageSharp {
                gatsbyImageData(width: 420, layout: CONSTRAINED)
              }
              publicURL
              extension
            }
          }
        }
      }
    }
  }
  locations: allWpLocation(sort: {fields: title}) {
    nodes {
      id
      link
      title
			birdeyeLocation {
				businessId
				timezone
				hoursOfOperations {
					day
					isOpen
					workingHours {
						startHour
						endHour
					}
				}
				location {
					lat
					lng
					address1
					address2
					city
					state
					zip
				}
				name
				phone
			}
      ACFLocation {
				googlePlaceId
        menuTitle
        bookMyAppointmentLink {
          target
          title
          url
        }
      }
    }
  }
  rightArrowYellowOffsite: file(name: {eq: "arrow_offsite"}) {
    childImageSharp {
      gatsbyImageData(width: 10, layout: FIXED)
    }
  }
  arrowLightGray: file(name: {eq: "arrow_light_gray"}) {
    childImageSharp {
      gatsbyImageData(width: 10, layout: FIXED)
    }
  }
  arrowRedLocal: file(name: {eq: "arrow_red_local"}) {
    childImageSharp {
      gatsbyImageData(width: 10, layout: FIXED)
    }
  }
  arrowDownGray: file(name: {eq: "arrow-down-gray"}) {
    childImageSharp {
      gatsbyImageData(width: 10, layout: FIXED)
    }
  }
  markerIcon: file(relativePath: {eq: "map-icon-RFD.png"}) {
    publicURL
    childImageSharp {
      gatsbyImageData(width: 15, placeholder: BLURRED, layout: FIXED)
    }
  }
}
`

export default LocationsTemplate;
